import { eventBus } from '../foundation';
import {
    ActionActionTypeEnum,
    SystemEventTypeEnum,
    TButtonData,
    TButtonMethodsRef,
    TCommonData,
    TPlayerActionData,
    TSubmitByEnterMethodsRef,
    TWidget,
} from '@libs/types';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

export const useButton = <T extends TCommonData>(widget: TWidget<T>, methodsRef: TButtonMethodsRef<T>) => {
    const isDefaultButton = useMemo(() => (widget?.data as TButtonData)?.default, [widget?.data]);
    const { isDisabled, triggerActionsByType, triggerActionsByNameAndType } = methodsRef;

    const handleClick = useCallback(
        (value?: TPlayerActionData) => {
            triggerActionsByType(ActionActionTypeEnum.closeBottomsheet).then();
            triggerActionsByType(ActionActionTypeEnum.onClick, { value }).then();
        },
        [triggerActionsByType],
    );

    const handleClickByActionName = useCallback(
        (actionName: string, value?: TPlayerActionData) => {
            triggerActionsByNameAndType(ActionActionTypeEnum.onClick, actionName, { value }).then();
        },
        [triggerActionsByNameAndType],
    );

    useEffect(() => {
        if (isDefaultButton) {
            eventBus.emitGeneral(SystemEventTypeEnum.IS_DEFAULT_BUTTON_ENABLED, !isDisabled);
        }
    }, [isDefaultButton, isDisabled]);

    return { handleClick, handleClickByActionName };
};

export const useSubmitByEnter = (submitWidget: TWidget<TButtonData> | undefined, methodsRef: TSubmitByEnterMethodsRef) => {
    const isEnabled = useRef(false);
    const { triggerActionsByType } = methodsRef;

    useEffect(() => {
        eventBus.subscribe(SystemEventTypeEnum.IS_DEFAULT_BUTTON_ENABLED, submitWidget?.uid ?? '', (value) => {
            isEnabled.current = !!value;
        });
        return () => {
            eventBus.unsubscribe(SystemEventTypeEnum.IS_DEFAULT_BUTTON_ENABLED, submitWidget?.uid ?? '');
        };
    }, [submitWidget?.uid]);
    return useCallback(
        (e: React.KeyboardEvent<HTMLDivElement>) => {
            const target = e.target as HTMLDivElement;
            if (target.tagName === 'INPUT' && isEnabled.current && e.key === 'Enter') {
                triggerActionsByType(ActionActionTypeEnum.onClick).then();
            }
        },
        [triggerActionsByType],
    );
};
